export type BookingDocumentType = 'preliminary' | 'voucher';

export interface BookingDocumentContext {
  booking_id: string;
  booking_reference?: string | null;
  booking_created_at: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time?: string | null;
  check_out_time?: string | null;
  room_type?: string | null;
  room_number?: string | null;
  nights: number;
  total_amount: number;
  paid_amount: number;
  remaining: number;
  first_night_price?: number | null;
  first_night_price_verified: boolean;
  booking_version: number;
}

export interface BookingMessageValues {
  guest_name: string;
  booking_created_at: string;
  check_in_date: string;
  check_out_date: string;
  check_in_time: string;
  check_out_time: string;
  room_number: string;
  room_type: string;
  nights: string;
  first_night_price: string;
  total_amount: string;
  paid_amount: string;
  remaining: string;
  payment_method: string;
  booking_reference: string;
  hotel_name: string;
  bank_name: string;
  payment_phone: string;
  recipient_name: string;
  contact_phone: string;
  email: string;
  card_info: string;
  bank_details: string;
  qr_url: string;
  prepayment_instructions: string;
  transfer_text: string;
  signature: string;
}

export const PLACEHOLDERS: Array<{ key: keyof BookingMessageValues; label: string }> = [
  { key: 'guest_name', label: 'ФИО гостя' },
  { key: 'booking_created_at', label: 'Дата бронирования' },
  { key: 'check_in_date', label: 'Дата заезда' },
  { key: 'check_out_date', label: 'Дата выезда' },
  { key: 'check_in_time', label: 'Время заезда' },
  { key: 'check_out_time', label: 'Время выезда' },
  { key: 'room_number', label: 'Номер комнаты' },
  { key: 'room_type', label: 'Категория номера' },
  { key: 'nights', label: 'Количество ночей' },
  { key: 'first_night_price', label: 'Цена первых суток' },
  { key: 'total_amount', label: 'Итого' },
  { key: 'paid_amount', label: 'Внесено' },
  { key: 'remaining', label: 'Остаток' },
  { key: 'payment_method', label: 'Способ оплаты' },
  { key: 'booking_reference', label: 'Номер брони' },
  { key: 'hotel_name', label: 'Название отеля' },
  { key: 'bank_name', label: 'Банк' },
  { key: 'payment_phone', label: 'Телефон для оплаты' },
  { key: 'recipient_name', label: 'Получатель' },
  { key: 'contact_phone', label: 'Телефон отеля' },
  { key: 'email', label: 'Email отеля' },
  { key: 'card_info', label: 'Карта для оплаты' },
  { key: 'bank_details', label: 'Банковские реквизиты' },
  { key: 'qr_url', label: 'Ссылка оплаты СБП' },
  { key: 'prepayment_instructions', label: 'Инструкции по предоплате' },
  { key: 'transfer_text', label: 'Текст о трансфере' },
  { key: 'signature', label: 'Подпись' },
];

export const DEFAULT_PRELIMINARY_TEMPLATE = `Уважаемый(ая) *{guest_name}*!

🏨 Предварительное бронирование «{hotel_name}»:

*Заезд:* {check_in_date} с {check_in_time}
*Выезд:* {check_out_date} до {check_out_time}
*Номер:* №{room_number}, {room_type} — {first_night_price} / сутки
*Оплата:* {payment_method}

Для подтверждения бронирования необходима предоплата за первые сутки — {first_night_price}

{card_info}
{prepayment_instructions}

🎫 После получения оплаты отправим ваучер с подтверждением бронирования

{signature}`;

export const DEFAULT_VOUCHER_TEMPLATE = `❤️ Благодарим Вас за заявку и настоящим подтверждаем бронирование, согласно следующей информации:

*Дата бронирования:* {booking_created_at}
*ФИО гостя:* {guest_name}
*Дата заезда:* {check_in_date}
*Дата выезда:* {check_out_date}
*Время заезда/выезда:* {check_in_time} / {check_out_time}
*Категория номера:* {room_type}
*Оплата:* {payment_method}

*Итого:* {total_amount}
*Внесено:* {paid_amount}
*Осталось внести:* {remaining}

{transfer_text}

{signature}`;

const aliases: Record<string, keyof BookingMessageValues> = {
  check_in: 'check_in_date',
  check_out: 'check_out_date',
  price_per_night: 'first_night_price',
  booking_ref: 'booking_reference',
  booking_date: 'booking_created_at',
  phone: 'contact_phone',
};

export function formatMoney(value: number | null | undefined, currency = '₽'): string {
  const amount = Number(value ?? 0);
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${formatted} ${currency}`;
}

export function formatDate(value: string): string {
  const dateOnly = value.slice(0, 10);
  const [year, month, day] = dateOnly.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
}

export function formatMoscowDate(value: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(value));
}

export function validateTemplate(source: string): string[] {
  const errors: string[] = [];
  const openCount = (source.match(/\{/g) || []).length;
  const closeCount = (source.match(/\}/g) || []).length;
  if (openCount !== closeCount) errors.push('Есть незакрытая фигурная скобка.');

  const allowed = new Set([...PLACEHOLDERS.map(item => item.key), ...Object.keys(aliases)]);
  const found = source.match(/\{([^{}]+)\}/g) || [];
  for (const token of found) {
    const key = token.slice(1, -1);
    if (!allowed.has(key)) errors.push(`Неизвестная переменная ${token}.`);
  }
  return [...new Set(errors)];
}

/**
 * Очищает финальный текст сообщения от повреждённых символов:
 * - одинокие суррогатные пары (битый UTF-16 после копипаста/импорта);
 * - символ замены U+FFFD ();
 * - невидимые управляющие символы (кроме \n и \t).
 * Нормальные эмодзи, суммы и переносы строк не затрагиваются.
 */
export function sanitizeMessageText(text: string): string {
  return text
    .normalize('NFC')
    // Корректные суррогатные пары (эмодзи) оставляем, одинокие суррогаты удаляем.
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF]|[\uDC00-\uDFFF]/g, match => match.length === 2 ? match : '')
    .replace(/\uFFFD/g, '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u0085]/g, '');
}

export function renderBookingMessage(source: string, values: BookingMessageValues): string {
  const renderLine = (line: string) => line.replace(/\{([^{}]+)\}/g, (token, rawKey: string) => {
    const key = (aliases[rawKey] || rawKey) as keyof BookingMessageValues;
    return key in values ? values[key] : token;
  });

  const rendered = source
    .split('\n')
    .map(line => ({ original: line, rendered: renderLine(line) }))
    // Убираем только строки, где ВСЕ переменные пустые (например, "Ссылка: {qr_url}" без ссылки).
    // Строки с заполненными переменными сохраняем, даже если они заканчиваются двоеточием.
    .filter(({ original, rendered }) => {
      const tokens = original.match(/\{([^{}]+)\}/g);
      if (!tokens) return true;
      const allEmpty = tokens.every(token => {
        const key = (aliases[token.slice(1, -1)] || token.slice(1, -1)) as keyof BookingMessageValues;
        return !(key in values) || values[key] === '';
      });
      return !allEmpty && rendered.trim() !== '';
    })
    .map(({ rendered }) => rendered)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return sanitizeMessageText(rendered);
}

export function buildTemplateValues(
  context: BookingDocumentContext,
  guestName: string,
  template: Record<string, unknown> | null,
  hotelName: string,
  firstNightOverride?: number | null,
): BookingMessageValues {
  const firstNight = context.first_night_price_verified
    ? context.first_night_price
    : firstNightOverride;
  const text = (key: string) => typeof template?.[key] === 'string' ? String(template[key]) : '';
  const cardInfo = text('card_number')
    ? `💳 ${text('bank_name') || 'Оплата'}: ${text('card_number')}${text('card_holder') ? ` 👤 Получатель: ${text('card_holder')}` : ''}`
    : '';
  const bankDetails = [
    text('company_name'), text('bank_account') ? `р/с ${text('bank_account')}` : '',
    text('inn') ? `ИНН ${text('inn')}` : '', text('bank_name') ? `Банк: ${text('bank_name')}` : '',
    text('bik') ? `БИК ${text('bik')}` : '', text('correspondent_account') ? `к/с ${text('correspondent_account')}` : '',
  ].filter(Boolean).join('\n');

  return {
    guest_name: guestName,
    booking_created_at: formatMoscowDate(context.booking_created_at),
    check_in_date: formatDate(context.check_in_date), check_out_date: formatDate(context.check_out_date),
    check_in_time: context.check_in_time || '—', check_out_time: context.check_out_time || '—',
    room_number: context.room_number || 'не назначен', room_type: context.room_type || 'не указана',
    nights: String(context.nights), first_night_price: firstNight == null ? '' : formatMoney(firstNight),
    total_amount: formatMoney(context.total_amount), paid_amount: formatMoney(context.paid_amount), remaining: formatMoney(context.remaining),
    payment_method: 'при заселении', booking_reference: context.booking_reference || '—', hotel_name: hotelName || 'Отель',
    bank_name: text('bank_name'), payment_phone: text('card_number'), recipient_name: text('card_holder'),
    contact_phone: text('phone'), email: text('email'), card_info: cardInfo, bank_details: bankDetails,
    qr_url: text('qr_payment_url'), prepayment_instructions: text('prepayment_instructions'),
    transfer_text: 'Прошу сообщить время прибытия, а также номер поезда и вагона для трансфера, если добираетесь не на автомобиле.',
    signature: `⚓️ С уважением, администратор гостиничного комплекса «${hotelName || 'Отель'}»`,
  };
}